/** Events (#705): the groups of an event's people, the dates an event runs, and the list's order, search and count line. */
import type { Sort } from "../../kit";
import type { EventGroup, EventRow } from "../../data/events";
import { fmtDate, shortDate } from "../../data/format";

/** What "Not back yet" means: CE21 records registrations, not who came. */
export const NOT_BACK_TIP = "Registered for the last edition, not this one";

/** The groups, in the order the page offers them. */
export const GROUPS: ReadonlyArray<{ value: EventGroup; label: string; hint?: string }> = [
  { value: "everyone", label: "Everyone" },
  { value: "members", label: "Members" },
  { value: "non_members", label: "Non-members" },
  { value: "vendors", label: "Vendors" },
  { value: "came_last_time", label: "Not back yet", hint: NOT_BACK_TIP },
];

/** The group an address asks for; anything else is Everyone. */
export function groupIn(value: string | null): EventGroup {
  return GROUPS.find((g) => g.value === value)?.value ?? "everyone";
}

/** "26 Nov 2026", "25 Sep – 27 Sep 2026", "30 Dec 2026 – 1 Jan 2027"; "No date" when CE21 gives none. */
export function dateSpan(starts: string | null, ends: string | null): string {
  if (!starts) return "No date";
  if (!ends || ends === starts) return fmtDate(starts);
  return starts.slice(0, 4) === ends.slice(0, 4) ? `${shortDate(starts)} – ${fmtDate(ends)}` : `${fmtDate(starts)} – ${fmtDate(ends)}`;
}

/** The columns a list of events can be sorted by, and how each reads a row. */
const SORT_VALUE: Record<string, (e: EventRow) => string | number | null> = {
  name: (e) => e.name,
  date: (e) => e.starts_on,
  registered: (e) => e.registered,
  members: (e) => e.members,
  non_members: (e) => e.non_members,
  vendors: (e) => e.vendors,
};

/** The events in the order asked for; unsorted, the server's: upcoming soonest first, then past latest first. An event with no value sorts last either way. */
export function sortEvents(events: readonly EventRow[], sort: Sort | null): EventRow[] {
  const read = sort && SORT_VALUE[sort.key];
  if (!sort || !read) return [...events];
  const sign = sort.dir === "asc" ? 1 : -1;
  return [...events].sort((a, b) => {
    const [x, y] = [read(a), read(b)];
    if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
    return (typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y), "en-US", { numeric: true })) * sign;
  });
}

/** Every word of the search in the event's name, case-insensitive. */
export function matchesEvent(event: EventRow, query: string): boolean {
  const name = event.name.toLowerCase();
  return query.toLowerCase().split(/\s+/).filter(Boolean).every((w) => name.includes(w));
}

/** "44 events · 3 upcoming", or "2 of 44 events" once a search narrows it. */
export function eventsLine(all: readonly EventRow[], shown: readonly EventRow[]): string {
  const n = (count: number) => count.toLocaleString("en-US");
  const events = (count: number) => `${n(count)} ${count === 1 ? "event" : "events"}`;
  if (shown.length !== all.length) return `${n(shown.length)} of ${events(all.length)}`;
  const upcoming = all.filter((e) => e.upcoming).length;
  return upcoming > 0 ? `${events(all.length)} · ${n(upcoming)} upcoming` : events(all.length);
}
